import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import {
  approveBudgetCenter,
  fetchBudgetCenter,
  fetchBudgetSubmissions,
  markBudgetCenterLive,
} from '@src/api/actions/dashboard/clientBudgets';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import paths from '@src/paths';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatCurrency(value) {
  const num = Number(value || 0);
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDateTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function getStatusColor(status) {
  switch (status) {
    case 'pending_approval': return 'warning';
    case 'approved': return 'info';
    case 'active': return 'success';
    case 'draft': return 'default';
    default: return 'default';
  }
}

function getStatusLabel(status) {
  switch (status) {
    case 'pending_approval': return 'Pending Approval';
    case 'approved': return 'Approved';
    case 'active': return 'Live';
    case 'draft': return 'Draft';
    default: return status;
  }
}

function StatChip({ label, value, color = 'default' }) {
  return (
    <Chip
      label={`${label}: ${value}`}
      color={color}
      size="small"
      variant={color === 'default' ? 'outlined' : 'filled'}
    />
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function BudgetSubmissionsPage() {
  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete();

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending_approval');

  // Action loading
  const [actionLoading, setActionLoading] = useState(null);

  // Approve dialog
  const [approveDialog, setApproveDialog] = useState(null); // { client_id, period, client_name, period_label }
  const [approving, setApproving] = useState(false);

  // Mark live dialog
  const [markLiveDialog, setMarkLiveDialog] = useState(null);
  const [markingLive, setMarkingLive] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Data Loading ─────────────────────────────────────────────────

  const loadSubmissions = useCallback(async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { page: pageNum };
      if (filterClientId) params.client_id = filterClientId;
      if (filterStatus) params.status = filterStatus;
      const res = await fetchBudgetSubmissions(params);
      setSubmissions(res.data?.results || []);
      setTotalCount(res.data?.count || 0);
      setTotalPages(Math.ceil((res.data?.count || 0) / 50));
    } catch {
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [filterClientId, filterStatus, page]);

  useEffect(() => {
    setPage(1);
    loadSubmissions(1);
  }, [filterClientId, filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadSubmissions(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Stats ────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const pending = submissions.filter((s) => s.status === 'pending_approval').length;
    const approved = submissions.filter((s) => s.status === 'approved').length;
    const active = submissions.filter((s) => s.status === 'active').length;
    const draft = submissions.filter((s) => s.status === 'draft').length;
    return { pending, approved, active, draft, total: totalCount };
  }, [submissions, totalCount]);

  // ─── Actions ──────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!approveDialog) return;
    setApproving(true);
    try {
      // Fetch current budget data to get segments
      const centerRes = await fetchBudgetCenter({
        client_id: approveDialog.client_id,
        period: approveDialog.period,
      });
      const pageData = centerRes.data;
      const segments = (pageData?.segments || []).map((seg) => ({
        budget_id: seg.budget_id || null,
        source_budget_id: seg.source_budget_id || null,
        vertical: seg.vertical || '',
        location: seg.location || '',
        channel: seg.channel || '',
        amount: seg.amount || '0',
        notes: seg.notes || '',
      }));

      await approveBudgetCenter({
        client_id: approveDialog.client_id,
        period: approveDialog.period,
        segments,
      });
      showSnack(`${approveDialog.client_name} ${approveDialog.period_label} budget approved.`);
      setApproveDialog(null);
      loadSubmissions(page);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to approve.';
      showSnack(detail, 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleMarkLive = async () => {
    if (!markLiveDialog) return;
    setMarkingLive(true);
    try {
      await markBudgetCenterLive({
        client_id: markLiveDialog.client_id,
        period: markLiveDialog.period,
      });
      showSnack(`${markLiveDialog.client_name} ${markLiveDialog.period_label} budget is now live.`);
      setMarkLiveDialog(null);
      loadSubmissions(page);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to mark live.';
      showSnack(detail, 'error');
    } finally {
      setMarkingLive(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4">Budget Submissions</Typography>
          {stats.pending > 0 && (
            <Chip label={`${stats.pending} pending`} color="warning" size="small" />
          )}
        </Stack>
      </Box>

      {/* Quick Stats */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <StatChip label="Total" value={stats.total} />
        <StatChip label="Pending Approval" value={stats.pending} color="warning" />
        <StatChip label="Approved" value={stats.approved} color="info" />
        <StatChip label="Live" value={stats.active} color="success" />
        <StatChip label="Draft" value={stats.draft} />
      </Stack>

      <Card>
        <CardContent>
          {/* Filters */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findOptionByValue(filterClientId)}
              onChange={(_, val) => setFilterClientId(val ? String(val.value) : '')}
              onInputChange={handleClientSearch}
              renderInput={(params) => (
                <TextField {...params} label="Client" size="small" sx={{ minWidth: 220 }} />
              )}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending_approval">Pending Approval</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="active">Live</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={() => loadSubmissions(1)} disabled={loading}>
              {loading ? <CircularProgress size={18} /> : 'Refresh'}
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : submissions.length === 0 ? (
            <Alert severity="info">No budget submissions found matching your filters.</Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                      <TableCell>Client</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Segments</TableCell>
                      <TableCell align="right">Total Budget</TableCell>
                      <TableCell>Comments</TableCell>
                      <TableCell>Last Activity</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {submissions.map((row) => (
                      <TableRow key={`${row.client_id}-${row.period}`} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.client_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.period_label}</TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(row.status)}
                            color={getStatusColor(row.status)}
                            size="small"
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell align="right">{row.segment_count}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total_amount)}</TableCell>
                        <TableCell>
                          {row.comment_count > 0 ? (
                            <Chip
                              icon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
                              label={row.comment_count}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ) : (
                            <Typography color="text.secondary" variant="caption">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.last_activity ? (
                            <Tooltip title={row.last_activity.summary || ''}>
                              <Stack spacing={0.25}>
                                <Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>
                                  {row.last_activity.summary}
                                </Typography>
                                <Typography color="text.secondary" variant="caption">
                                  {row.last_activity.actor?.name || row.last_activity.actor?.email || 'System'} · {formatDateTime(row.last_activity.created_at)}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          ) : (
                            <Typography color="text.secondary" variant="caption">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {row.status === 'pending_approval' ? (
                              <Button
                                size="small"
                                color="success"
                                variant="contained"
                                startIcon={actionLoading === `approve-${row.client_id}-${row.period}` ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                                disabled={actionLoading !== null}
                                onClick={() => setApproveDialog({
                                  client_id: row.client_id,
                                  period: row.period,
                                  client_name: row.client_name,
                                  period_label: row.period_label,
                                  total_amount: row.total_amount,
                                  segment_count: row.segment_count,
                                })}
                              >
                                Approve
                              </Button>
                            ) : null}
                            {row.status === 'approved' ? (
                              <Button
                                size="small"
                                color="success"
                                variant="contained"
                                startIcon={actionLoading === `live-${row.client_id}-${row.period}` ? <CircularProgress size={14} color="inherit" /> : <RocketLaunchIcon />}
                                disabled={actionLoading !== null}
                                onClick={() => setMarkLiveDialog({
                                  client_id: row.client_id,
                                  period: row.period,
                                  client_name: row.client_name,
                                  period_label: row.period_label,
                                  total_amount: row.total_amount,
                                  segment_count: row.segment_count,
                                })}
                              >
                                Mark Live
                              </Button>
                            ) : null}
                            <Tooltip title="Open in Budget Center">
                              <Button
                                size="small"
                                variant="outlined"
                                href={`${paths.budgetCenter}?client_id=${row.client_id}&period=${row.period}`}
                                startIcon={<OpenInNewIcon />}
                              >
                                View
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Approve Confirmation Dialog */}
      <Dialog fullWidth maxWidth="sm" open={Boolean(approveDialog)} onClose={() => setApproveDialog(null)}>
        <DialogTitle>Approve Budget</DialogTitle>
        <DialogContent>
          {approveDialog ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Approve the <strong>{approveDialog.period_label}</strong> budget for <strong>{approveDialog.client_name}</strong>?
              </Typography>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography color="text.secondary" variant="caption">Total Budget</Typography>
                  <Typography variant="h6">{formatCurrency(approveDialog.total_amount)}</Typography>
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption">Segments</Typography>
                  <Typography variant="h6">{approveDialog.segment_count}</Typography>
                </Box>
              </Stack>
              <Alert severity="info">
                This will approve the budget. You will still need to mark it live separately.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleApprove}
            disabled={approving}
            startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
          >
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Live Confirmation Dialog */}
      <Dialog fullWidth maxWidth="sm" open={Boolean(markLiveDialog)} onClose={() => setMarkLiveDialog(null)}>
        <DialogTitle>Mark Budget Live</DialogTitle>
        <DialogContent>
          {markLiveDialog ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="body2">
                Mark the <strong>{markLiveDialog.period_label}</strong> budget for <strong>{markLiveDialog.client_name}</strong> as live?
              </Typography>
              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography color="text.secondary" variant="caption">Total Budget</Typography>
                  <Typography variant="h6">{formatCurrency(markLiveDialog.total_amount)}</Typography>
                </Box>
                <Box>
                  <Typography color="text.secondary" variant="caption">Segments</Typography>
                  <Typography variant="h6">{markLiveDialog.segment_count}</Typography>
                </Box>
              </Stack>
              <Alert severity="warning">
                This action cannot be undone. The budget will immediately become active and the client will be notified.
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkLiveDialog(null)}>Cancel</Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleMarkLive}
            disabled={markingLive}
            startIcon={markingLive ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchIcon />}
          >
            Confirm — Mark Live
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        open={snackbar.open}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
